// ** Icon imports
import {
  Compass,
  Truck,
  ShoppingBag,
  Tag,
  CreditCard,
  Map,
  PenTool,
  Settings,
  HelpCircle,
  ShoppingCart,
  Star,
  Link,
  Droplet,
  Zap,
  Package,
  Users,
  BarChart2,
  Download
} from 'react-feather'
import { HandPointing } from '@phosphor-icons/react'

// ** Type import
import { VerticalNavItemsType } from 'src/@core/layouts/types'
import { t } from 'i18next'
import { Flask } from '@phosphor-icons/react'
import { FeatureProfile } from 'src/lib/types/random'
import { useUser } from 'src/lib/contexts/UserContext'


type GetNavigationItemTypes = {
  isOnboardingComplete: boolean
  userConfigured: boolean
  hasOverdueInvoices: boolean
  profile?: FeatureProfile
  isSuperAdmin?: boolean
}

export const getNavigationItems = ({
  isOnboardingComplete,
  userConfigured,
  hasOverdueInvoices,
  profile = FeatureProfile.SHOPFLO,
  isSuperAdmin
}: GetNavigationItemTypes): VerticalNavItemsType => {

  const isDisabled = !isOnboardingComplete || !userConfigured || hasOverdueInvoices

  const shopfloNavItems = [
    {
      title: t('analytics.title'),
      icon: BarChart2,
      path: '/analytics',
      disabled: isDisabled && !isSuperAdmin,
      rbacPermission: 'TAB_ANALYTICS'
    },
    {
      title: t('overview'),
      icon: Compass,
      path: '/overview',
      disabled: isDisabled,
      rbacPermission: 'TAB_OVERVIEW'
    },
    {
      title: t('orders'),
      icon: Truck,
      path: '/orders',
      disabled: isDisabled,
      rbacPermission: 'TAB_ORDERS'
    },
    {
      title: t('abandoned_checkouts'),
      icon: Package,
      path: '/abandoned-checkouts',
      disabled: isDisabled,
      rbacPermission: 'TAB_ABANDONED_CHECKOUTS'
    },
    {
      title: t('upsell'),
      icon: ShoppingBag,
      path: '/upsell',
      disabled: isDisabled,
      rbacPermission: 'TAB_UPSELL'
    },
    {
      title: t('discounts'),
      icon: Tag,
      path: '/discounts',
      disabled: isDisabled,
      rbacPermission: 'TAB_DISCOUNTS'
    },
    {
      title: t('experiments'),
      icon: Flask,
      path: '/experiments',
      disabled: isDisabled,
      rbacPermission: 'TAB_EXPERIMENTS'
    },
    {
      title: t('automations'),
      icon: Zap,
      path: '/automations',
      disabled: isDisabled,
      rbacPermission: 'TAB_AUTOMATIONS'
    },
    {
      title: t('segments'),
      icon: Users,
      path: '/segments',
      disabled: isDisabled,
      rbacPermission: 'TAB_SEGMENTS'
    },
    {
      title: t('payment_offers'),
      icon: CreditCard,
      path: '/payment-offers',
      disabled: isDisabled,
      rbacPermission: 'TAB_PAYMENT_OFFERS'
    },
    {
      title: t('rewards'),
      icon: Star,
      path: '/rewards',
      disabled: isDisabled,
      rbacPermission: 'TAB_REWARDS'
    },
    {
      title: t('redeem_rewards'),
      icon: Star,
      path: '/redeem-rewards',
      disabled: isDisabled,
      rbacPermission: 'TAB_REDEEM_REWARDS'
    },
    {
      title: t('shipping'),
      icon: Map,
      path: '/shipping',
      disabled: isDisabled,
      rbacPermission: 'TAB_SHIPPING_DELIVERY'
    },
    {
      title: t('shipping_zones'),
      icon: Map,
      path: '/shipping-delivery',
      disabled: isDisabled,
      rbacPermission: 'TAB_SHIPPING_DELIVERY_V2'
    },
    {
      title: t('rto_suite'),
      icon: Droplet,
      path: '/rto-suite',
      disabled: isDisabled,
      rbacPermission: 'TAB_RTO_SUITE'
    },
    {
      title: t('customize_ui'),
      icon: PenTool,
      path: '/style',
      disabled: isDisabled,
      rbacPermission: 'TAB_CUSTOMISE_UI'
    },
    {
      title: t('one_click_add_on'),
      icon: HandPointing,
      path: '/one-click-add-on',
      disabled: isDisabled,
      rbacPermission: 'TAB_ONE_CLICK_ADDON',
      iconProp: { weight: 'bold' }
    },
    {
      title: t('settings'),
      icon: Settings,
      path: '/settings',
      rbacPermission: 'TAB_SETTINGS',
      disabled: hasOverdueInvoices
    },
    {
      title: t('Download Reports'),
      icon: Download,
      path: '/download/report',
      disabled: isDisabled,
      rbacPermission: 'TAB_DOWNLOAD_REPORTS'
    },
    {
      title: t('category_insights'),
      icon: BarChart2,
      path: '/insights',
      rbacPermission: 'TAB_CATEGORIES_INSIGHTS'
    },
    {
      title: t('support'),
      icon: HelpCircle,
      path: '/support',
      rbacPermission: 'TAB_SUPPORT',
      disabled: !userConfigured || hasOverdueInvoices
    },
    {
      title: t('partners'),
      icon: Link,
      path: '/partners',
      rbacPermission: 'TAB_PARTNERS',
      disabled: !userConfigured || hasOverdueInvoices
    },
    {
      title: 'Widgets',
      icon: Tag,
      path: '/cart/widget',
      disabled: isDisabled,
      rbacPermission: 'TAB_CART'
    },
    {
      title: 'Customize UI',
      icon: PenTool,
      path: '/cart/ui',
      disabled: isDisabled,
      rbacPermission: 'TAB_CART'
    },
    {
      title: 'Cart Analytics',
      icon: Compass,
      path: '/cart/cart-analytics',
      disabled: isDisabled,
      rbacPermission: 'TAB_CART'
    },
    {
      title: 'App Store',
      path: '/app-store',
      rbacPermission: 'TAB_APP_STORE',
      disabled: isDisabled,
      hidden: true
    }
  ]

  const shoppassNavItems = [
    {
      title: 'Shop Pass',
      icon: Link,
      path: '/sso/analytics',
      rbacPermission: 'TAB_SSO',
      disabled: isDisabled
    }
  ]

  return (profile === FeatureProfile.SHOPFLO ? shopfloNavItems : shoppassNavItems) as VerticalNavItemsType
}
